import { useState } from 'react';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReply } from '@fortawesome/pro-solid-svg-icons';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { NSButton, NSCard } from 'bricks';
import NSUserAvatar from 'bricks/NSUserAvatar/NSUserAvatar';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import useToast from 'hooks/useToast';
import { formatUTCtoLocal } from 'ns_libs/formatter';
import { IUser } from 'types/user';
import { TimelineComment } from 'views/DealDashboard/Overview/Timeline/types';
import { useDeleteComment } from 'views/DealDashboard/Overview/Timeline/hooks/useDeleteComment';
import './CommentItem.scss';

export interface CommentItemProps {
    comment: TimelineComment;
    orgId: string;
    dealId: number;
    timelineId: number;
    usersByCorrelationId: Map<string, IUser>;
    currentUserCorrelationId: string | null;
    onReply: () => void;
}

const CommentItem = ({
    comment, orgId, dealId, timelineId, usersByCorrelationId, currentUserCorrelationId, onReply,
}: CommentItemProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const { showError } = useToast();
    const { mutate: deleteComment } = useDeleteComment();

    const anchorId = `comment-${comment.id}`;

    if (comment.deletedAt !== null) {
        return (
            <div id={anchorId} className="d-flex align-items-center p-2 my-2 text-muted font-italic font-13">
                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                This comment was deleted
                <span className="ml-2 font-12">{formatUTCtoLocal(comment.deletedAt)}</span>
            </div>
        );
    }

    const author = usersByCorrelationId.get(comment.authorUserCorrelationId);
    const authorName = comment.authorName ?? author?.name ?? 'Unknown';
    const canDelete = currentUserCorrelationId !== null
        && comment.authorUserCorrelationId === currentUserCorrelationId;

    const handleConfirmDelete = () => {
        deleteComment(
            {
                orgId, dealId, timelineId, commentId: comment.id,
            },
            { onError: () => showError('Failed to delete comment. Please try again.') },
        );
        setIsConfirmOpen(false);
    };

    return (
        <NSCard
            id={anchorId}
            className="CommentItem NSCard--level-3 d-flex flex-row align-items-start p-2 my-2"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <NSUserAvatar
                size={36}
                radius={18}
                className="mr-2"
                userName={authorName}
                userPicture={author?.picture}
            />
            <div className="flex-grow-1">
                <div className="d-flex align-items-baseline">
                    <span className="text-white font-weight-semibold">{authorName}</span>
                    <span className="text-muted font-12 ml-2">{formatUTCtoLocal(comment.createdAt)}</span>
                </div>
                <div className="CommentItem__body text-white">
                    {parse(DOMPurify.sanitize(comment.bodyHtml))}
                </div>
            </div>

            {isHovered && (
                <div className="d-flex align-items-center align-self-center">
                    <NSButton
                        color="link"
                        className="text-muted hover:text-white p-0 px-2"
                        icon={faReply}
                        ariaLabel="Reply"
                        callback={onReply}
                    />
                    {canDelete && (
                        <NSButton
                            color="link"
                            className="text-muted hover:text-danger p-0 px-2"
                            icon={faTrash}
                            ariaLabel="Delete comment"
                            callback={() => setIsConfirmOpen(true)}
                        />
                    )}
                </div>
            )}

            <ConfirmationModal
                isOpen={isConfirmOpen}
                danger
                toggle={() => setIsConfirmOpen(false)}
                onCancel={() => setIsConfirmOpen(false)}
                onConfirm={handleConfirmDelete}
                modalHeaderText="Delete comment?"
                modalFooterButtonText="Delete"
                modalFooterButtonColor="danger"
            >
                <p>Are you sure you want to delete this comment? This action cannot be undone.</p>
            </ConfirmationModal>
        </NSCard>
    );
};

export default CommentItem;
